import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useRelayEnvironment } from 'react-relay';
import { commitLocalUpdate } from 'relay-runtime';

const HOVER_DEBOUNCE_DURATION = 100;

class CellRefRelayMap {
	cellRefRelayMap = new Map<HTMLTableCellElement, string>();

	set = (cell: HTMLTableCellElement, relayFieldId: string) => {
		this.cellRefRelayMap.set(cell, relayFieldId);
	};

	delete = (cell: HTMLTableCellElement) => {
		this.cellRefRelayMap.delete(cell);
	};

	get = (cell: HTMLTableCellElement) => {
		return this.cellRefRelayMap.get(cell);
	};
}

const cellRefRelayMap = new CellRefRelayMap();

export const setCellRefRelayMap = cellRefRelayMap.set;
export const deleteCellRefRelayMap = cellRefRelayMap.delete;
export const getCellRefRelayMap = cellRefRelayMap.get;

export const useEventDelegator = () => {
	const environment = useRelayEnvironment();
	const currentHoveredCellRelayIdRef = useRef<string | null>(null);

	const setHoverFocusState = useCallback(
		(relayFieldId: string) => {
			commitLocalUpdate(environment, (store) => {
				const newRecord = store.get(relayFieldId);
				if (newRecord) {
					newRecord
						.getOrCreateLinkedRecord('hoverOrFocusState', 'JiraFieldHoverOrFocusState')
						?.setValue(true, 'isHoveredOrFocused');
					currentHoveredCellRelayIdRef.current = relayFieldId;
				}
			});
		},
		[environment],
	);

	const clearHoverFocusState = useCallback(() => {
		commitLocalUpdate(environment, (store) => {
			if (currentHoveredCellRelayIdRef.current) {
				const previousRecord = store.get(currentHoveredCellRelayIdRef.current);
				if (previousRecord) {
					previousRecord
						.getOrCreateLinkedRecord('hoverOrFocusState', 'JiraFieldHoverOrFocusState')
						?.setValue(false, 'isHoveredOrFocused');
					currentHoveredCellRelayIdRef.current = null;
				}
			}
		});
	}, [environment]);

	const debouncedSetHoverFocusState = useMemo(
		() =>
			debounce((relayFieldId: string) => setHoverFocusState(relayFieldId), HOVER_DEBOUNCE_DURATION),
		[setHoverFocusState],
	);

	const handleMouseOver = useCallback(
		(event: React.MouseEvent<HTMLTableElement>) => {
			if (event.target instanceof HTMLElement) {
				const target = event.target;
				// Get closest cell that is being hovered over
				const cell = target.closest('td');
				// If no cell is found, clear the hover state and return
				if (!cell || !(cell instanceof HTMLTableCellElement)) {
					clearHoverFocusState();
					return;
				}
				const relayFieldId = cellRefRelayMap.get(cell);

				// If the cell is the same as the current hovered cell, return
				if (relayFieldId && relayFieldId !== currentHoveredCellRelayIdRef.current) {
					// If the cell is different from the current hovered cell, clear the previous hover state
					if (currentHoveredCellRelayIdRef.current) {
						clearHoverFocusState();
					}

					// Finally, set the hover state for the new cell
					debouncedSetHoverFocusState(relayFieldId);
				}
			}
		},
		[clearHoverFocusState, debouncedSetHoverFocusState],
	);

	const handleMouseLeave = useCallback(() => {
		clearHoverFocusState();
		debouncedSetHoverFocusState.cancel();
	}, [clearHoverFocusState, debouncedSetHoverFocusState]);

	useEffect(() => {
		return () => {
			if (currentHoveredCellRelayIdRef.current) {
				clearHoverFocusState();
			}
			debouncedSetHoverFocusState.cancel();
		};
	}, [clearHoverFocusState, debouncedSetHoverFocusState]);

	return {
		handleMouseLeave,
		handleMouseOver,
	};
};

// Returns a ref to bind to the table cell.
// It adds the table cell element and its corresponding Relay field ID to the cellRefRelayMap.
// lastNode ensures that a cell's ref/relay field ID is removed from the map when the cell is unmounted.
// This is used by the event listener to look up the corresponding relay field ID for the cell.
export const useEventDelegatorRef = (relayFieldId?: string) => {
	const lastNode = useRef<HTMLTableCellElement | null>(null);

	const tableCellRef = useCallback(
		(node: HTMLTableCellElement | null) => {
			if (node && relayFieldId) {
				setCellRefRelayMap(node, relayFieldId);
				lastNode.current = node;
			} else if (lastNode.current) {
				deleteCellRefRelayMap(lastNode.current);
				lastNode.current = null;
			}
		},
		[relayFieldId],
	);

	return tableCellRef;
};
